// Lightweight stylized India map with major-city pins.
// Coordinates are tuned for a 600x720 viewBox (approximate, not geographically precise).
import { MapPin } from "lucide-react";
import { useState } from "react";

// Simplified India outline path (stylized, not survey-accurate).
const INDIA_PATH = "M298 38 C314 32 330 36 348 44 C376 56 402 76 410 102 C418 124 412 148 422 168 C430 184 448 196 462 214 C480 236 486 264 498 290 C512 320 524 350 520 382 C516 410 500 436 484 460 C470 484 446 502 430 526 C414 552 408 580 388 598 C368 614 340 614 318 624 C302 632 286 644 268 656 C250 668 230 680 208 678 C190 676 178 660 168 642 C154 614 152 580 154 548 C156 514 162 480 156 446 C150 412 132 384 124 350 C116 318 124 286 134 256 C144 226 158 198 174 172 C190 144 208 116 226 90 C242 66 264 46 298 38 Z";

const CITIES = [
  { name: "Delhi NCR", x: 268, y: 184, count: 142 },
  { name: "Mumbai", x: 200, y: 392, count: 128 },
  { name: "Bengaluru", x: 268, y: 538, count: 96 },
  { name: "Hyderabad", x: 296, y: 458, count: 78 },
  { name: "Chennai", x: 318, y: 568, count: 74 },
  { name: "Pune", x: 222, y: 416, count: 68 },
  { name: "Kolkata", x: 412, y: 308, count: 62 },
  { name: "Ahmedabad", x: 196, y: 290, count: 52 },
  { name: "Jaipur", x: 234, y: 234, count: 41 },
  { name: "Lucknow", x: 320, y: 232, count: 36 },
  { name: "Chandigarh", x: 264, y: 144, count: 28 },
  { name: "Indore", x: 246, y: 308, count: 24 },
  { name: "Bhubaneswar", x: 390, y: 372, count: 22 },
  { name: "Kochi", x: 252, y: 596, count: 18 },
  { name: "Guwahati", x: 466, y: 234, count: 16 },
];

export default function IndiaMap() {
  const [active, setActive] = useState(null);
  const max = Math.max(...CITIES.map((c) => c.count));

  return (
    <div className="relative w-full" data-testid="india-map">
      <svg viewBox="0 0 600 720" className="w-full h-auto max-h-[560px]" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="india-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#E6F0FF" />
            <stop offset="100%" stopColor="#CCE0FF" />
          </linearGradient>
          <radialGradient id="pin-glow">
            <stop offset="0%" stopColor="#FF7A00" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FF7A00" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Dot grid background */}
        <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1" fill="#CBD5E1" opacity="0.5" />
        </pattern>
        <rect width="600" height="720" fill="url(#dots)" />

        {/* India outline */}
        <path d={INDIA_PATH} fill="url(#india-grad)" stroke="#003DA5" strokeWidth="2" opacity="0.9" />

        {/* City pins */}
        {CITIES.map((c) => {
          const size = 6 + (c.count / max) * 14;
          const isActive = active === c.name;
          return (
            <g
              key={c.name}
              onMouseEnter={() => setActive(c.name)}
              onMouseLeave={() => setActive(null)}
              style={{ cursor: "pointer" }}
              data-testid={`map-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {isActive && <circle cx={c.x} cy={c.y} r={size + 14} fill="url(#pin-glow)" />}
              <circle cx={c.x} cy={c.y} r={size} fill="#FF7A00" opacity="0.18" />
              <circle cx={c.x} cy={c.y} r={size * 0.5} fill="#FF7A00" />
              <circle cx={c.x} cy={c.y} r={2.5} fill="white" />
              {isActive && (
                <g>
                  <rect x={c.x + 12} y={c.y - 28} rx="6" ry="6" width="148" height="44" fill="#0B1220" />
                  <text x={c.x + 22} y={c.y - 10} fill="white" fontSize="13" fontWeight="700" fontFamily="Plus Jakarta Sans">
                    {c.name}
                  </text>
                  <text x={c.x + 22} y={c.y + 8} fill="#FF7A00" fontSize="11" fontWeight="600" fontFamily="Plus Jakarta Sans">
                    {c.count} workshops
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-xl border border-slate-200 px-4 py-2.5 shadow-sm">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Workshop density</div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#FF7A00]" />
          <div className="text-xs text-slate-600">Low</div>
          <div className="w-3 h-3 rounded-full bg-[#FF7A00]" />
          <div className="text-xs text-slate-600">Mid</div>
          <div className="w-4 h-4 rounded-full bg-[#FF7A00]" />
          <div className="text-xs text-slate-600">High</div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
        <MapPin className="w-3.5 h-3.5 text-[#FF7A00]" />
        Hover any pin for details · {CITIES.length} of 100+ cities shown
      </div>
    </div>
  );
}
